import React, { useState, useContext, JSX } from 'react';
import { Button, Form, Message } from 'semantic';
import { Container, Modal } from 'semantic-ui-react';
import SearchContext from './Context';
import { useTranslation } from 'react-i18next';
import { startOfMonth, subDays, startOfDay, endOfDay } from 'date-fns';
import { zonedTimeToUtc } from 'date-fns-tz';

import { ApiError } from 'utils/api';
import { Flex } from '../../common/layout/helpers';

type ExportFormat = 'csv' | 'xlsx';

type ExportFromToModalProps = {
  format?: ExportFormat;
  trigger: React.ReactNode;
  onRangeSubmit: (params: {
    from: Date;
    to: Date;
    format: ExportFormat;
    filters?: { [key: string]: any };
    granularity?: 'month' | 'year';
  }) => Promise<void>;
};

export default function ExportRangeModal({
  trigger,
  format,
  onRangeSubmit,
}: ExportFromToModalProps): JSX.Element {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError>();
  const [from, setFrom] = useState(startOfMonth(new Date()));
  const [to, setTo] = useState(subDays(new Date(), 1));
  const [granularity, setGranularity] = useState<'month' | 'year'>('month');
  const context = useContext<any>(SearchContext);

  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

  const handleSubmit = async () => {
    setLoading(true);
    setError(undefined);

    try {
      const fromTz = zonedTimeToUtc(startOfDay(from), tz);
      const toTz = zonedTimeToUtc(endOfDay(to), tz);
      await onRangeSubmit({
        from: fromTz,
        to: toTz,
        format: format || 'csv',
        granularity,
        filters: { ...context?.filters },
      });

      setLoading(false);
    } catch (err) {
      setLoading(false);
      setError(err as ApiError);
    }
  };

  return (
    <Modal
      closeIcon
      trigger={trigger}
      size="mini"
      header={t('export.ExportData', 'Export Data')}
      content={
        <Container style={{ padding: '1em' }}>
          <Form onSubmit={handleSubmit} error={true}>
            <Form.Input
              label={t('export.From', 'From')}
              name="from"
              type="date"
              value={from.toISOString().split('T')[0]}
              placeholder={t('common.noStart', 'No Start')}
              onChange={(e, { name, value }) => {
                const date = new Date(value);
                if (date.toString() === 'Invalid Date') return;
                setFrom(date);
              }}
              fluid
              clearable
            />

            <Form.Input
              name="to"
              label={t('export.To', 'To')}
              fluid
              type="date"
              value={to.toISOString().split('T')[0]}
              placeholder={t('common.present', 'Present')}
              onChange={(e, { name, value }) => {
                const date = new Date(value);
                if (date.toString() === 'Invalid Date') return;
                setTo(date);
              }}
              clearable
            />

            <Form.Dropdown
              name="granularity"
              label={t('export.Granularity', 'Granularity')}
              options={[
                {
                  key: 'monthly',
                  text: t('export.Monthly', 'Monthly'),
                  value: 'month',
                },
                {
                  key: 'yearly',
                  text: t('export.Yearly', 'Yearly'),
                  value: 'year',
                },
              ]}
              value={granularity}
              onChange={(e, { name, value }) => {
                setGranularity(value as 'month' | 'year');
              }}
              selection
              fluid
            />

            {error && <Message error content={error.message} />}
            <Flex justify="end" style={{ marginTop: '1em' }}>
              <Button
                type="submit"
                onClick={(e: any) => {
                  e.stopPropagation();
                }}
                loading={loading}
                primary
                content={
                  format == 'csv'
                    ? t('common.exportCSVFile', 'Export CSV File')
                    : t('common.exportXLSXFile', 'Export Excel File')
                }
              />
            </Flex>
          </Form>
        </Container>
      }
    />
  );
}
